import React, { useContext, useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/shadcn/dialog";
import { SearchBar } from "@/components/SearchBar";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { cn } from "@/lib/utils";
import {
  Navigation,
  MousePointer,
  Type,
  Camera,
  Clock,
  Download,
  Code,
  ArrowDown,
  Bot,
  Keyboard,
  Cookie,
  Eraser,
  FileDown,
  FileUp,
  Globe,
  RefreshCw,
  Search,
  LayoutGrid,
  ListChecks,
  ArrowLeftRight,
  StopCircle,
  Terminal,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PlaybookEditorContext } from "./playbookEditorContext";

interface AddStepModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (step: any) => void;
  insideLoop?: boolean;
}

interface StepTemplate {
  id: string;
  label: string;
  icon: LucideIcon;
  color: string;
  template: Record<string, unknown>;
}

const STEP_TEMPLATES: Record<string, StepTemplate[]> = {
  Navigation: [
    {
      id: "navigate",
      label: "Navigate to URL",
      icon: Globe,
      color: "#f9fafb",
      template: {
        type: "navigate",
        url: "https://example.com",
        wait_until: "load",
      },
    },
    {
      id: "reload",
      label: "Reload Page",
      icon: RefreshCw,
      color: "#f9fafb",
      template: { type: "reload", wait_until: "load" },
    },
    {
      id: "switch_tab",
      label: "Switch Tab",
      icon: ArrowLeftRight,
      color: "#f9fafb",
      template: { type: "switch_tab", by: "latest", match: "contains" },
    },
  ],
  Interaction: [
    {
      id: "click",
      label: "Click Element",
      icon: MousePointer,
      color: "#f9fafb",
      template: {
        type: "click",
        selector: { primary: "", fallbacks: [], must_be_visible: true },
        button: "left",
      },
    },
    {
      id: "type",
      label: "Type Text",
      icon: Type,
      color: "#f9fafb",
      template: {
        type: "type",
        selector: { primary: "", fallbacks: [], must_be_visible: true },
        text: "",
        delay: 50,
        clear_first: false,
      },
    },
    {
      id: "fill",
      label: "Fill Input",
      icon: Type,
      color: "#f9fafb",
      template: {
        type: "fill",
        selector: { primary: "", fallbacks: [], must_be_visible: true },
        value: "",
      },
    },
    {
      id: "select",
      label: "Select Option",
      icon: ListChecks,
      color: "#f9fafb",
      template: {
        type: "select",
        selector: { primary: "", fallbacks: [], must_be_visible: true },
        value: "",
        by: "value",
      },
    },
    {
      id: "press",
      label: "Press Key",
      icon: Keyboard,
      color: "#f9fafb",
      template: { type: "press", key: "Enter" },
    },
    {
      id: "upload",
      label: "Upload File",
      icon: FileUp,
      color: "#f9fafb",
      template: {
        type: "upload",
        selector: { primary: "", fallbacks: [], must_be_visible: true },
        file_variable: "",
      },
    },
  ],
  Extraction: [
    {
      id: "extract",
      label: "Extract Data",
      icon: Download,
      color: "#f9fafb",
      template: {
        type: "extract",
        name: "extracted_data",
        selector: { primary: "", fallbacks: [], must_be_visible: true },
        extract_type: "text",
      },
    },
    {
      id: "screenshot",
      label: "Take Screenshot",
      icon: Camera,
      color: "#f9fafb",
      template: { type: "screenshot", name: "screenshot", full_page: false },
    },
    {
      id: "download",
      label: "Download File",
      icon: FileDown,
      color: "#f9fafb",
      template: {
        type: "download",
        name: "downloaded_file",
        url: "",
        trigger_selector: null,
      },
    },
    {
      id: "extract_request_headers",
      label: "Extract Request Headers",
      icon: Download,
      color: "#f9fafb",
      template: {
        type: "extract_request_headers",
        name: "request_headers",
        url_pattern: "",
      },
    },
    {
      id: "extract_url",
      label: "Extract Current URL",
      icon: Globe,
      color: "#f9fafb",
      template: { type: "extract_url", name: "current_url" },
    },
    {
      id: "set_variable",
      label: "Log Value",
      icon: FileDown,
      color: "#f9fafb",
      template: { type: "set_variable", name: "my_variable", value: "" },
    },
  ],
  Cookies: [
    {
      id: "extract_cookies",
      label: "Get Cookies",
      icon: Cookie,
      color: "#f9fafb",
      template: { type: "extract_cookies", name: "cookies", domain: "" },
    },
    {
      id: "set_cookies",
      label: "Set Cookies",
      icon: Cookie,
      color: "#f9fafb",
      template: {
        type: "set_cookies",
        cookies: [{ name: "", value: "" }],
        from_variable: "",
      },
    },
    {
      id: "clear_cookies",
      label: "Clear Cookies",
      icon: Eraser,
      color: "#f9fafb",
      template: { type: "clear_cookies", domain: "" },
    },
  ],
  Wait: [
    {
      id: "wait_for",
      label: "Wait For Element",
      icon: Clock,
      color: "#f9fafb",
      template: {
        type: "wait_for",
        selector: { primary: "", fallbacks: [], must_be_visible: true },
        state: "visible",
      },
    },
    {
      id: "wait_for_timeout",
      label: "Wait (Timeout)",
      icon: Clock,
      color: "#f9fafb",
      template: { type: "wait_for_timeout", timeout: 1000 },
    },
    {
      id: "wait_for_load",
      label: "Wait for Page Load",
      icon: Clock,
      color: "#f9fafb",
      template: { type: "wait_for_load", wait_until: "load" },
    },
  ],
  Control: [
    {
      id: "if",
      label: "If Condition",
      icon: Code,
      color: "#f9fafb",
      template: {
        type: "if",
        condition: {
          type: "element_exists",
          selector: { primary: "", fallbacks: [], must_be_visible: true },
        },
        then: [],
        otherwise: [],
      },
    },
    {
      id: "loop",
      label: "Loop",
      icon: Code,
      color: "#f9fafb",
      template: {
        type: "loop",
        over: "count",
        count: 5,
        steps: [],
      },
    },
    {
      id: "break",
      label: "Break Loop",
      icon: StopCircle,
      color: "#f9fafb",
      template: {
        type: "break",
        condition: {
          type: "element_exists",
          selector: { primary: "", fallbacks: [], must_be_visible: true },
        },
      },
    },
    {
      id: "scroll",
      label: "Scroll",
      icon: ArrowDown,
      color: "#f9fafb",
      template: { type: "scroll", to: "bottom" },
    },
  ],
  AI: [
    {
      id: "agent",
      label: "AI Agent",
      icon: Bot,
      color: "#f9fafb",
      template: { type: "agent", prompt: "", name: "agent_result" },
    },
  ],
  Code: [
    {
      id: "run_script",
      label: "Run Playwright script",
      icon: Terminal,
      color: "#f9fafb",
      template: {
        type: "run_script",
        language: "python",
        script:
          "# page, context, browser, inputs and outputs are in scope\noutputs['result'] = page.title()\n",
        inputs: [],
        outputs: ["result"],
      },
    },
  ],
};

// Only the Editor V2 node can run scripts.
const V2_ONLY_categories = new Set(["Code"]);

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  All: LayoutGrid,
  Navigation: Navigation,
  Interaction: MousePointer,
  Extraction: Download,
  Cookies: Cookie,
  Wait: Clock,
  Control: Code,
  AI: Bot,
  Code: Terminal,
};

export function AddStepModal({
  open,
  onClose,
  onAdd,
  insideLoop = false,
}: AddStepModalProps) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const { v2NodeId } = useContext(PlaybookEditorContext);

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedCategory("All");
    }
  }, [open]);

  const visibleTemplates = useMemo(() => {
    const result: Record<string, StepTemplate[]> = {};
    for (const [category, templates] of Object.entries(STEP_TEMPLATES)) {
      if (V2_ONLY_categories.has(category) && !v2NodeId) continue;
      result[category] = insideLoop
        ? templates
        : templates.filter((t) => t.id !== "break");
    }
    return result;
  }, [insideLoop, v2NodeId]);

  const categories = useMemo(
    () => ["All", ...Object.keys(visibleTemplates)],
    [visibleTemplates]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const result: StepTemplate[] = [];
    for (const [category, templates] of Object.entries(visibleTemplates)) {
      if (selectedCategory !== "All" && category !== selectedCategory) continue;
      for (const t of templates) {
        if (t.label.toLowerCase().includes(q)) result.push(t);
      }
    }
    return result;
  }, [search, selectedCategory, visibleTemplates]);

  const handleSelectTemplate = (template: StepTemplate) => {
    const stepId = `${template.id}_${Date.now()}`;
    onAdd({ id: stepId, ...template.template, description: "" });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[900px] p-0 gap-0 overflow-hidden">
        <div className="flex h-[580px]">
          {/* Sidebar */}
          <div className="w-[220px] border-r border-border flex flex-col shrink-0 bg-muted/30">
            <div className="px-5 pt-5 pb-4">
              <DialogTitle className="text-md font-semibold">
                Add Step
              </DialogTitle>
            </div>
            <nav className="flex flex-col gap-1 px-3 flex-1">
              {categories.map((cat) => {
                const CatIcon = CATEGORY_ICONS[cat];
                const isActive = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors text-left cursor-pointer",
                      isActive
                        ? "bg-secondary text-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 font-normal"
                    )}
                  >
                    {CatIcon && <CatIcon size={16} className="shrink-0" />}
                    {cat === "All" ? "All actions" : cat}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main content */}
          <div className="flex flex-col flex-1 overflow-hidden gap-4">
            <h1 className="text-md font-medium text-primary px-5 pt-5">
              {selectedCategory === "All" ? "Actions" : selectedCategory}
            </h1>
            {/* Search bar */}
            <div className="px-5 shrink-0 mb-2">
              <SearchBar
                placeholder="Search actions..."
                value={search}
                setValue={(value: string) => setSearch(value)}
              />
            </div>

            {/* Step grid */}
            <div
              className="flex flex-col gap-8 overflow-y-auto flex-1 px-5 pr-1 -mr-1 pb-5"
              style={{ scrollbarGutter: "stable" }}
            >
              {selectedCategory !== "All" && filtered.length > 0 ? (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    {selectedCategory}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filtered.map((t) => (
                      <StepCard
                        key={t.id}
                        template={t}
                        onClick={handleSelectTemplate}
                      />
                    ))}
                  </div>
                </div>
              ) : search && filtered.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filtered.map((t) => (
                    <StepCard
                      key={t.id}
                      template={t}
                      onClick={handleSelectTemplate}
                    />
                  ))}
                </div>
              ) : !search ? (
                <>
                  {Object.entries(visibleTemplates).map(
                    ([category, templates]) => (
                      <div key={category}>
                        <h3 className="text-sm font-medium text-muted-foreground mb-3">
                          {category}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {templates.map((t) => (
                            <StepCard
                              key={t.id}
                              template={t}
                              onClick={handleSelectTemplate}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </>
              ) : null}

              {filtered.length === 0 && (
                <div className="mb-6 h-full">
                  <NoxusEmptyBlock
                    title="No actions"
                    description={
                      search ? (
                        <>No actions found for &ldquo;{search}&rdquo;.</>
                      ) : (
                        "No actions found for the selected category."
                      )
                    }
                    icon={Search}
                    size="sm"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StepCard({
  template,
  onClick,
}: {
  template: StepTemplate;
  onClick: (template: StepTemplate) => void;
}) {
  const Icon = template.icon;
  return (
    <div
      className="flex items-center gap-3 p-3 pr-4 rounded-lg border border-border bg-card shadow-xs cursor-pointer hover:border-ring"
      onClick={() => onClick(template)}
    >
      <div
        className="shrink-0 w-9 h-9 rounded-lg border border-border/40 flex items-center justify-center"
        style={{ backgroundColor: template.color }}
      >
        <Icon size={18} className="text-foreground/70" />
      </div>
      <span className="font-medium text-sm text-foreground truncate">
        {template.label}
      </span>
    </div>
  );
}
